import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '../components/ui/Form';
import { Input } from '../components/ui/Input';

const RegisterPage = ({ onSwitchToLogin }) => {
  const { theme, register } = useAppContext();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [user_type, setUserType] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  const types = ["admin", "regular"];

  const validatePassword = (pwd) => {
    if (pwd.length < 12) return "Password must be at least 12 characters long.";
    if (!/[A-Z]/.test(pwd)) return "Password must contain at least one uppercase letter.";
    if (!/[a-z]/.test(pwd)) return "Password must contain at least one lowercase letter.";
    if (!/\d/.test(pwd)) return "Password must contain at least one digit.";
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) return "Password must contain at least one special character.";
    return null;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!user_type) {
      setError('Please select a user type.');
      return;
    }

    if (password.length > 72) {
      setError("Password must be 72 characters or less.");
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    try {
      const response = await register(username, email, password, user_type);
      setSuccess(response.message);
      setTimeout(() => onSwitchToLogin(), 2000); 
    } catch (err) {
      setError(err.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`w-full max-w-md transition-colors ${theme === 'dark' ? 'border-gray-800 text-white' : 'border-gray-200 text-black'}`}>
      <form onSubmit={handleRegister}>
        <FieldGroup>
          <FieldSet>
            <FieldLegend>Create an Account</FieldLegend>
            <FieldDescription>Join LuminAI to get started.</FieldDescription>
          </FieldSet>
          <FieldSet>
            <Field>
              <FieldLabel htmlFor="user-type">User Type</FieldLabel>
              <select
                id="user-type"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={user_type}
                onChange={(e) => setUserType(e.target.value)}
              >
                <option value="" disabled>Select user type</option>
                {types.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </Field>
            <Field>
              <FieldLabel htmlFor="username">Username</FieldLabel>
              <Input id="username" type="text" required value={username} onChange={e => setUsername(e.target.value)} />
            </Field>
            <Field>
              <FieldLabel htmlFor="email">Email Address</FieldLabel>
              <Input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} />
            </Field>
            <Field>
              <FieldLabel htmlFor="password">Password</FieldLabel>
              <Input id="password" type="password" required maxLength="72" value={password} onChange={e => setPassword(e.target.value)} />
              <p className="text-xs text-gray-500 mt-1">
                Min 12 chars, with uppercase, lowercase, digit, and special character
              </p>
            </Field>
          </FieldSet>
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          {success && <p className="text-sm text-green-500 text-center">{success}</p>}
          <button type="submit" disabled={loading} className="w-full h-10 inline-flex items-center justify-center rounded-md bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
          <p className="text-center text-sm text-gray-500">
            Already have an account?{' '}
            <button type="button" onClick={onSwitchToLogin} className="font-semibold text-blue-500 hover:underline">Sign In</button>
          </p>
        </FieldGroup>
      </form>
    </div>
  );
};

export default RegisterPage;